/**
 * Rich text editor built on Tiptap. Supports bold/italic/lists/colour
 * formatting with a minimal toolbar. Stores and returns HTML strings for
 * easy rendering without Tiptap on the read side.
 *
 * Drop-in replacement for <Textarea> in places where basic formatting is needed.
 */

import { useRef } from 'react'
import { EditorContent, useEditor, useEditorState } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { Color, TextStyle } from '@tiptap/extension-text-style'
import Placeholder from '@tiptap/extension-placeholder'
import { Bold, Italic, List, ListOrdered } from 'lucide-react'

import { Button } from '@/frontend/components/ui/button'
import { cn } from '@/frontend/lib/utils'
import { inputStyles } from '@/frontend/components/ui/input'
import { Toggle } from '@/frontend/components/ui/toggle'

const COLOURS = [
  { label: 'Red', value: '#dc2626' },
  { label: 'Blue', value: '#2563eb' },
  { label: 'Green', value: '#16a34a' },
] as const

export interface RichTextEditorProps {
  /** HTML string to hydrate the editor with on mount. */
  value?: string
  /** Called with an HTML string whenever content changes. */
  onChange?: (html: string) => void
  placeholder?: string
  /** Applied on mount only — changing after mount has no effect. */
  disabled?: boolean
  className?: string
  /** Renders a Cancel button in the toolbar when provided. */
  onCancel?: () => void
  /** Renders a Save button in the toolbar when provided. */
  onSave?: () => void
  /** Disables the Save button (e.g. while a mutation is in flight). */
  isSaving?: boolean
}

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  onCancel,
  onSave,
  isSaving,
}: RichTextEditorProps) {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
        strike: false,
        // Auto-link URLs as users type/paste, and open them in a new tab with
        // safe rel attributes so window.opener can't be exploited.
        link: {
          openOnClick: false,
          autolink: true,
          defaultProtocol: 'https',
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
          },
        },
      }),
      TextStyle,
      Color,
      ...(placeholder
        ? [Placeholder.configure({ placeholder })]
        : []),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      onChangeRef.current?.(e.getHTML())
    },
  })

  return (
    <div
      className={cn(
        // `min-w-0` lets the editor shrink inside flex/grid parents instead of
        // being forced wide by long unbreakable content like URLs. Combined
        // with `break-all` on anchors below, this keeps pasted links from
        // blowing out the surrounding layout.
        inputStyles,
        'min-w-0 focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50',
        disabled && 'cursor-not-allowed opacity-75 border-dashed',
        className,
      )}
      onKeyDown={(e) => {
        if (e.key !== 'Escape') e.stopPropagation()
      }}
    >
      {editor && (
        <Toolbar
          editor={editor}
          disabled={disabled}
          onCancel={onCancel}
          onSave={onSave}
          isSaving={isSaving}
        />
      )}
      <EditorContent
        editor={editor}
        className={cn(
          'min-h-16 cursor-text',
          disabled && 'bg-muted',
          '[&_.tiptap]:outline-none [&_.tiptap]:min-h-full [&_.tiptap]:px-2.5 [&_.tiptap]:py-2',
          // Long URLs (auto-linked by Tiptap into <a>) must break anywhere so
          // they don't blow out the editor's width. Regular prose is unaffected.
          // The text-primary + underline pair restores visible link styling that
          // Tailwind preflight strips — links are visible but inert inside the
          // editor (clicking just places the cursor). Read view handles nav.
          '[&_.tiptap_a]:break-all [&_.tiptap_a]:text-primary [&_.tiptap_a]:underline',
          '[&_.tiptap_ul]:list-disc [&_.tiptap_ul]:ml-4',
          '[&_.tiptap_ol]:list-decimal [&_.tiptap_ol]:ml-4',
          // Tailwind preflight strips <p> margins. Give consecutive paragraphs
          // a small breathing gap but let each <p> contribute its own line of
          // vertical space — so pressing Enter twice produces ~one blank line,
          // three times produces ~two, etc. (predictable, gmail-style).
          '[&_.tiptap_p+p]:mt-1',
          '[&_.tiptap_p.is-editor-empty:first-child::before]:text-muted-foreground [&_.tiptap_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)] [&_.tiptap_p.is-editor-empty:first-child::before]:float-left [&_.tiptap_p.is-editor-empty:first-child::before]:h-0 [&_.tiptap_p.is-editor-empty:first-child::before]:pointer-events-none',
        )}
      />
    </div>
  )
}

interface ToolbarProps {
  editor: NonNullable<ReturnType<typeof useEditor>>
  disabled?: boolean
  onCancel?: () => void
  onSave?: () => void
  isSaving?: boolean
}

function Toolbar({ editor, disabled, onCancel, onSave, isSaving }: ToolbarProps) {
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => {
      if (!e) return { isBold: false, isItalic: false, isBullet: false, isOrdered: false, colour: '' }
      return {
        isBold: e.isActive('bold'),
        isItalic: e.isActive('italic'),
        isBullet: e.isActive('bulletList'),
        isOrdered: e.isActive('orderedList'),
        colour: (e.getAttributes('textStyle').color as string) || '',
      }
    },
  })

  return (
    <div className="flex items-center gap-0.5 border-b px-1.5 py-1">
      <Toggle
        size="sm"
        pressed={state.isBold}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleBold().run()}
        aria-label="Bold"
      >
        <Bold className="size-3.5" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={state.isItalic}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleItalic().run()}
        aria-label="Italic"
      >
        <Italic className="size-3.5" />
      </Toggle>
      <div className="mx-0.5 h-4 w-px bg-border" />
      <Toggle
        size="sm"
        pressed={state.isBullet}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleBulletList().run()}
        aria-label="Bullet list"
      >
        <List className="size-3.5" />
      </Toggle>
      <Toggle
        size="sm"
        pressed={state.isOrdered}
        disabled={disabled}
        onPressedChange={() => editor.chain().focus().toggleOrderedList().run()}
        aria-label="Numbered list"
      >
        <ListOrdered className="size-3.5" />
      </Toggle>
      <div className="mx-0.5 h-4 w-px bg-border" />
      {COLOURS.map(({ label, value }) => (
        <Toggle
          key={value}
          size="sm"
          pressed={state.colour === value}
          disabled={disabled}
          onPressedChange={() => {
            if (state.colour === value) {
              editor.chain().focus().unsetColor().run()
            } else {
              editor.chain().focus().setColor(value).run()
            }
          }}
          aria-label={label}
        >
          <span className="size-3 rounded-full" style={{ backgroundColor: value }} />
        </Toggle>
      ))}
      {(onCancel || onSave) && (
        <>
          <div className="flex-1" />
          <div className="flex items-center gap-1">
            {onCancel && (
              <Button variant="ghost" onClick={onCancel} disabled={isSaving}>
                Cancel
              </Button>
            )}
            {onSave && (
              <Button onClick={onSave} disabled={isSaving}>
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}


